import {
    Box,
    ButtonGroup,
    Table as ChakraTable,
    createListCollection,
    Flex,
    HStack,
    IconButton,
    Pagination,
    Portal,
    Progress,
    Select,
    Skeleton,
    Spacer,
    Stack,
    Text,
    VisuallyHidden,
} from '@chakra-ui/react';
// Keep this dep's range in lockstep with @tanstack/react-table's own
// @tanstack/react-store range: a second resolved copy would silently break
// cross-copy atom dependency tracking (same class of hazard as the jose pin).
import { shallow, useSelector } from '@tanstack/react-store';
import type { Table as ReactTable, RowData } from '@tanstack/react-table';
import {
    columnVisibilityFeature,
    FlexRender,
    metaHelper,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    tableFeatures,
} from '@tanstack/react-table';
import pluralize from 'pluralize';
import {
    HiArrowDown,
    HiArrowUp,
    HiChevronLeft,
    HiChevronRight,
} from 'react-icons/hi';

export interface TableColumnMeta {
    /**
     * Marks the column's body cells as PII: they render with
     * data-sentry-mask so Sentry Session Replay keeps them redacted
     * inside the otherwise-unmasked app shell.
     */
    pii?: boolean;
    /**
     * How many skeleton bars the column's cells show while the table is
     * loading (default 1). Set to the number of text lines the real cell
     * renders — e.g. 2 for Members' name + email — so the placeholder
     * rows self-size to the loaded row height.
     */
    skeletonLines?: number;
    /**
     * Fixed column width (any CSS width) — applied to the header cell
     * by TableHeaderRows (body cells inherit through the table's column
     * sizing). For icon/checkbox columns the auto layout would
     * otherwise stretch.
     */
    width?: string;
}

/**
 * The one feature set every table in the monorepo runs on: the three
 * behaviors these components render (sorting, pagination, selection) plus
 * column visibility, which gates getVisibleLeafColumns/getVisibleCells.
 * Column meta is typed here via the per-table columnMeta slot instead of
 * global declaration merging. Pass to useTable as `features` and to
 * createColumnHelper as the TFeatures argument.
 */
export const standardTableFeatures = tableFeatures({
    columnVisibilityFeature,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    columnMeta: metaHelper<TableColumnMeta>(),
});

export type StandardTableFeatures = typeof standardTableFeatures;

// Per-slice pagination subscription — the narrowest v9 read surface, live
// even when the caller narrows useTable's state selector. Both Paginator and
// Table subscribe, so a pagination change re-renders this whole subtree
// through Table; the caller's selector only governs re-renders for the other
// slices (sorting, selection, visibility).
function usePaginationSlice<TData extends RowData>(
    table: ReactTable<StandardTableFeatures, TData>,
) {
    return useSelector(table.atoms.pagination, (pagination) => pagination, {
        compare: shallow,
    });
}

// Deterministic width variation so skeleton cells read as real content of
// varying length instead of a uniform grid (stable across re-renders).
const SKELETON_CELL_WIDTHS = ['60%', '85%', '45%'];

/**
 * Cell props for fixed-height rows: the cell drops its vertical padding and
 * centers content vertically instead of letting padding drive the height.
 * A row still grows if its content is taller (td height is a minimum).
 * Shared with custom table bodies (e.g. the console's virtualized jobs
 * table) so both tables size rows identically.
 */
export const fixedRowCellProps = (rowHeight?: number) =>
    rowHeight
        ? ({
              height: `${rowHeight}px`,
              py: 0,
              verticalAlign: 'middle',
          } as const)
        : {};

/**
 * The sortable header rows shared by every table that follows this design:
 * ARIA sortable-table pattern on the th, the sort control as a real button
 * styled via table.recipe.ts ([data-sortable]), sort arrow right-aligned by
 * space-between. Exported so custom table bodies (e.g. the console's
 * virtualized jobs table) reuse the exact header look and behavior.
 */
export function TableHeaderRows<TData extends RowData>({
    table,
    isLoading = false,
}: {
    table: ReactTable<StandardTableFeatures, TData>;
    /** While loading the headers are display-only: real labels and sort
     * state, no interactions. */
    isLoading?: boolean;
}) {
    return table.getHeaderGroups().map((headerGroup) => {
        return (
            <ChakraTable.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    const canGetSort = header.column.getCanSort();

                    return (
                        <ChakraTable.ColumnHeader
                            key={header.id}
                            colSpan={header.colSpan}
                            scope='col'
                            // ARIA sortable-table pattern: sort state lives on
                            // the th — 'none' on the other sortable columns,
                            // absent entirely on unsortable ones.
                            aria-sort={
                                canGetSort
                                    ? isSorted === 'asc'
                                        ? 'ascending'
                                        : isSorted === 'desc'
                                          ? 'descending'
                                          : 'none'
                                    : undefined
                            }
                            p={0}
                            width={header.column.columnDef.meta?.width}
                        >
                            <Flex
                                // Only sortable headers are interactive — a
                                // plain div otherwise, so static headers
                                // don't become focusable do-nothing buttons.
                                as={canGetSort ? 'button' : undefined}
                                data-sortable={canGetSort ? true : undefined}
                                onClick={
                                    isLoading
                                        ? undefined
                                        : header.column.getToggleSortingHandler()
                                }
                                pointerEvents={isLoading ? 'none' : undefined}
                                align='center'
                                justify='space-between'
                                w='full'
                                px={4}
                                py={3}
                            >
                                <Text
                                    // div, not the default <p>: component
                                    // headers (e.g. a select-all checkbox)
                                    // nest block elements, invalid inside p.
                                    // Color inherits from the columnHeader
                                    // slot (muted at rest, fg on sortable
                                    // hover — a pinned color here would
                                    // block that cascade).
                                    as='div'
                                    mr={2}
                                    textStyle='body-2'
                                >
                                    <FlexRender header={header} />
                                </Text>
                                {/* Decorative — aria-sort on the th carries
                                    the sort state for assistive tech. */}
                                {isSorted === 'asc' && (
                                    <HiArrowUp aria-hidden />
                                )}
                                {isSorted === 'desc' && (
                                    <HiArrowDown aria-hidden />
                                )}
                            </Flex>
                        </ChakraTable.ColumnHeader>
                    );
                })}
            </ChakraTable.Row>
        );
    });
}

function TableComponent<TData extends RowData>({
    table,
    label,
    isRefetching,
    isLoading = false,
    skeletonRows = 10,
    emptyMessage = 'No data available',
    rowHeight,
}: {
    table: ReactTable<StandardTableFeatures, TData>;
    label: string;
    isRefetching: boolean;
    isLoading?: boolean;
    skeletonRows?: number;
    emptyMessage?: string;
    rowHeight?: number;
}) {
    const headers = <TableHeaderRows table={table} isLoading={isLoading} />;

    // Loading placeholder: a dummy table that mimics the loaded one — real
    // headers above, text-shaped skeleton bars below (one per line the real
    // cell renders, see TableColumnMeta.skeletonLines). Each thin bar is
    // vertically padded (my) up to the body-9 line height so dummy rows keep
    // the same pitch as their loaded counterparts. aria-hidden: the rows are
    // purely decorative — screen readers get the table's busy state instead.
    const skeletonBody = Array.from({ length: skeletonRows }, (_, rowIndex) => (
        <ChakraTable.Row key={rowIndex} aria-hidden>
            {table.getVisibleLeafColumns().map((column, columnIndex) => (
                <ChakraTable.Cell
                    key={column.id}
                    textStyle='body-9'
                    {...fixedRowCellProps(rowHeight)}
                >
                    {Array.from(
                        { length: column.columnDef.meta?.skeletonLines ?? 1 },
                        (_, lineIndex) => (
                            <Skeleton
                                key={lineIndex}
                                variant='shine'
                                height='3'
                                my='0.5'
                                maxW='12rem'
                                width={
                                    SKELETON_CELL_WIDTHS[
                                        (rowIndex + columnIndex + lineIndex) %
                                            SKELETON_CELL_WIDTHS.length
                                    ]
                                }
                            />
                        ),
                    )}
                </ChakraTable.Cell>
            ))}
        </ChakraTable.Row>
    ));

    const rowModel = table.getRowModel().rows;
    const rows = isLoading ? (
        skeletonBody
    ) : rowModel.length === 0 ? (
        <ChakraTable.Row>
            <ChakraTable.Cell
                colSpan={table.getAllColumns().length}
                textAlign='center'
                py={6}
                color='fg.muted'
                fontWeight='medium'
            >
                {emptyMessage}
            </ChakraTable.Cell>
        </ChakraTable.Row>
    ) : (
        rowModel.map((row) => (
            <ChakraTable.Row
                key={row.id}
                // Light: bg.subtle, a quiet step on the white rows (bg.hover
                // read stronger than the header band). Dark: the 5% tint.
                _hover={{ bg: { base: 'bg.subtle', _dark: 'bg.hover' } }}
            >
                {row.getVisibleCells().map((cell) => (
                    <ChakraTable.Cell
                        key={cell.id}
                        textStyle='body-9'
                        {...fixedRowCellProps(rowHeight)}
                        data-sentry-mask={
                            cell.column.columnDef.meta?.pii ? '' : undefined
                        }
                    >
                        <FlexRender cell={cell} />
                    </ChakraTable.Cell>
                ))}
            </ChakraTable.Row>
        ))
    );

    const isBusy = isLoading || isRefetching;

    return (
        <Box position='relative'>
            {/* Skeleton loads announce via role=status; background refetches
                stay quiet (aria-busy plus the named progressbar below) so a
                window refocus doesn't re-announce over unchanged rows. */}
            {isLoading && (
                <VisuallyHidden role='status'>Loading</VisuallyHidden>
            )}
            <ChakraTable.ScrollArea
                bg='bg.panel'
                borderWidth='1px'
                // 5px, as cloud-ui's and the console jobs tables.
                borderRadius='5px'
            >
                <ChakraTable.Root
                    size='md'
                    variant='outline'
                    interactive
                    aria-label={label}
                    aria-busy={isBusy || undefined}
                >
                    <ChakraTable.Header>{headers}</ChakraTable.Header>
                    <ChakraTable.Body>{rows}</ChakraTable.Body>
                </ChakraTable.Root>
            </ChakraTable.ScrollArea>
            {isRefetching && (
                <Progress.Root
                    value={null}
                    shape='square'
                    translations={{ value: () => 'Refreshing' }}
                    position='absolute'
                    top='1px'
                    insetX='1px'
                    // 6px-tall clip box whose 4px top radius mirrors the
                    // ScrollArea's inner border curve (5px radius − 1px
                    // border) so the sweep never paints over the corners.
                    height='6px'
                    overflow='hidden'
                    borderTopRadius='4px'
                    pointerEvents='none'
                    // CSS-only debounce: 'both' holds opacity 0 through the
                    // 0.3s delay, so sub-300ms refetches never show anything.
                    // Departure is the unmount — instant, no exit animation.
                    animation='fade-in 0.15s ease-out 0.3s both'
                    // The stock indeterminate gradient reads --track-color
                    // but nothing sets it for linear progress (and
                    // brand.accent is a semantic token, not a colorPalette),
                    // so feed it explicitly.
                    css={{ '--track-color': 'colors.brand.accent' }}
                >
                    <Progress.Track
                        height='0.5'
                        bgColor='transparent'
                        shadow='none'
                    >
                        {/* Transparent bgColor: the outline variant would
                            otherwise sweep a solid gray block (default
                            colorPalette) under the accent gradient. */}
                        <Progress.Range
                            bgColor='transparent'
                            _motionReduce={{
                                // Nested under _indeterminate to out-rank
                                // the recipe's sweep selector (a flat
                                // override silently loses): renders a
                                // static accent bar instead of the sweep.
                                _indeterminate: {
                                    animation: 'none',
                                    insetInlineStart: '0',
                                    minWidth: '100%',
                                    backgroundImage: 'none',
                                    bgColor: 'brand.accent',
                                },
                            }}
                        />
                    </Progress.Track>
                </Progress.Root>
            )}
        </Box>
    );
}

const PAGINATION_STEPS = [5, 10, 30, 50, 100, 200];

function Paginator<TData extends RowData>({
    table,
    collectionName,
    isLoading = false,
    pageSizeSteps = PAGINATION_STEPS,
}: {
    table: ReactTable<StandardTableFeatures, TData>;
    collectionName: string;
    isLoading?: boolean;
    pageSizeSteps?: Array<number>;
}) {
    const rowsPerPage = createListCollection({
        items: pageSizeSteps.map((step) => ({
            label: step.toString(),
            value: step.toString(),
        })),
    });
    const { pageIndex, pageSize } = usePaginationSlice(table);
    const count = table.getRowCount();
    const page = pageIndex + 1;

    return (
        <Flex
            flexDir={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'center' }}
            wrap='wrap'
            gap={2}
            // Keep the paginator's footprint while loading (no layout shift
            // when data lands) but make it display-only.
            pointerEvents={isLoading ? 'none' : undefined}
        >
            <Select.Root
                width={{ base: 'full', md: '300px' }}
                size='sm'
                collection={rowsPerPage}
                value={[pageSize.toString()]}
                onValueChange={(item) => {
                    table.setPageSize(Number(item.value));
                }}
            >
                <Select.HiddenSelect />
                <Flex gap={4} align='center'>
                    <Select.Label fontWeight='normal'>
                        {collectionName} per page
                    </Select.Label>
                    <Select.Control width='80px'>
                        <Select.Trigger>
                            <Select.ValueText />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                            <Select.Indicator />
                        </Select.IndicatorGroup>
                    </Select.Control>
                </Flex>
                <Portal>
                    <Select.Positioner>
                        <Select.Content>
                            {rowsPerPage.items.map((item) => (
                                <Select.Item item={item} key={item.value}>
                                    {item.label}
                                    <Select.ItemIndicator />
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Positioner>
                </Portal>
            </Select.Root>

            <Spacer />

            <Pagination.Root
                count={count}
                pageSize={pageSize}
                page={page}
                siblingCount={0}
                onPageChange={(e) => table.setPageIndex(e.page - 1)}
            >
                <HStack
                    spaceX={1}
                    width={{ base: 'full', md: 'auto' }}
                    justifyContent={{ base: 'space-between', md: 'flex-start' }}
                >
                    {isLoading ? (
                        <Skeleton variant='shine' height='3' width='8rem' />
                    ) : (
                        <Text textStyle='sm'>
                            {count === 0
                                ? `0 ${collectionName}`
                                : `${pageIndex * pageSize + 1} - ${Math.min((pageIndex + 1) * pageSize, count)} of ${count} ${collectionName}`}
                        </Text>
                    )}

                    <ButtonGroup attached size='sm' variant='ghost'>
                        <Pagination.PrevTrigger asChild>
                            <IconButton>
                                <HiChevronLeft />
                            </IconButton>
                        </Pagination.PrevTrigger>

                        <Pagination.Items
                            render={(pageItem) => (
                                <IconButton
                                    variant={{
                                        base: 'ghost',
                                        _selected: 'outline',
                                    }}
                                >
                                    {pageItem.value}
                                </IconButton>
                            )}
                        />

                        <Pagination.NextTrigger asChild>
                            <IconButton>
                                <HiChevronRight />
                            </IconButton>
                        </Pagination.NextTrigger>
                    </ButtonGroup>
                </HStack>
            </Pagination.Root>
        </Flex>
    );
}

type TableProps<TData extends RowData> = {
    table: ReactTable<StandardTableFeatures, TData>;
    /**
     * Accessible name for the table (applied as aria-label), so
     * screen-reader users land on e.g. "Members, table" rather than an
     * anonymous grid.
     */
    label: string;
    collectionName?: string;
    /**
     * Any in-flight fetch past the first load — page/sort/search transitions
     * (keepPreviousData holds the previous rows on screen), mutation
     * invalidation, window refocus. Drives the top-edge refetch bar.
     */
    isFetching: boolean;
    /**
     * First load (no data yet): render a display-only dummy table — real
     * column headers with sorting disabled, `skeletonRows` rows of skeleton
     * cells, and a de-activated paginator — approximating the loaded layout.
     */
    isLoading?: boolean;
    /**
     * Placeholder row count while `isLoading`. Defaults to the page size
     * capped at 10 — close to the loaded height for short lists without
     * painting a viewport-high shimmer wall on large page sizes. Columns
     * whose real cells span multiple text lines can set
     * `meta.skeletonLines` on their column definition so placeholder rows
     * self-size to the loaded row height.
     */
    skeletonRows?: number;
    paginable?: boolean;
    /**
     * Rows-per-page choices offered by the paginator. Override when the
     * backing endpoint caps the page size below the default steps.
     */
    pageSizeSteps?: Array<number>;
    emptyMessage?: string;
    /**
     * Fixed body-row height in px. Cells drop their vertical padding and
     * center content vertically; a row still grows if its content is
     * taller. Unset keeps the recipe's padding-driven sizing.
     */
    rowHeight?: number;
};

export function Table<TData extends RowData>({
    table,
    label,
    collectionName = 'rows',
    isFetching,
    isLoading = false,
    skeletonRows,
    paginable = true,
    pageSizeSteps,
    emptyMessage,
    rowHeight,
}: TableProps<TData>) {
    const { pageSize } = usePaginationSlice(table);
    return (
        <Stack>
            <TableComponent
                table={table}
                label={label}
                // The skeleton body is for the first load only; any later
                // fetch — page/sort/search transitions over rows held by
                // keepPreviousData, mutation invalidation, window refocus —
                // shows the top-edge progress bar over the current rows.
                isRefetching={isFetching && !isLoading}
                isLoading={isLoading}
                skeletonRows={skeletonRows ?? Math.min(pageSize, 10)}
                emptyMessage={emptyMessage}
                rowHeight={rowHeight}
            />
            {paginable && (
                <Paginator
                    table={table}
                    collectionName={pluralize(collectionName)}
                    isLoading={isLoading}
                    pageSizeSteps={pageSizeSteps}
                />
            )}
        </Stack>
    );
}
